import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { FaDownload, FaFileUpload } from 'react-icons/fa';

function BulkUploadModal({
  isOpen,
  onClose,
  onSubmit, // Expects async function: (file, agentId) => Promise<void>
  agents = [], // Optional: For agent selection dropdown
  loadingAgents = false, // Optional: Loading state for agents
  fixedAgentId = null, // Optional: If provided, agent dropdown is hidden/disabled
  fixedAgentName = '', // Optional: Name to display when agent is fixed
}) {
  const [bulkFile, setBulkFile] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setBulkFile(null);
      setError('');
      setUploading(false);
      // Reset selected agent only if it's not fixed
      if (!fixedAgentId) {
        setSelectedAgentId('');
      }
    }
  }, [isOpen, fixedAgentId]);

  const handleFileChange = (e) => {
    setBulkFile(e.target.files[0]);
    setError(''); // Clear error when file changes
  };

  const handleAgentSelectChange = (selectedOption) => {
    setSelectedAgentId(selectedOption ? selectedOption.value : '');
    setError(''); // Clear error when agent changes
  };

  const handleDownloadTemplate = () => {
    // Generate CSV template content
    const csvTemplate = "name,email,mobile,case_type,product_id\nJohn Doe,john.doe@example.com,9876543210,Credit Card,1\nJane Smith,jane.smith@example.com,9876543211,Home Loan,\nAlex Brown,alex.brown@example.com,9876543212,Personal Loan,3";

    // Create a Blob from the CSV string
    const blob = new Blob([csvTemplate], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lead_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleInternalSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!bulkFile) {
      setError('Please select a file to upload');
      return;
    }

    const agentIdToSubmit = fixedAgentId || selectedAgentId;

    if (!agentIdToSubmit) {
      setError('Please select an agent to assign these leads to');
      return;
    }

    setUploading(true);
    try {
      // Call the parent's submit handler
      await onSubmit(bulkFile, agentIdToSubmit);
      // If onSubmit succeeds without throwing, the parent handles success messages.
      // We can optionally close the modal here after success.
      // onClose(); // Let parent handle closing for now, or uncomment if preferred.
    } catch (err) {
      console.error('Error during bulk upload submission:', err);
      setError(err.message || 'Failed to upload leads. Please check the file format and try again.');
    } finally {
      setUploading(false);
    }
  };

  const agentOptions = agents.map(agent => ({
    value: agent.id.toString(),
    label: agent.name
  }));

  const currentAgentOption = selectedAgentId ?
    agentOptions.find(opt => opt.value === selectedAgentId)
    : null;

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Bulk Upload Leads {fixedAgentName ? `for ${fixedAgentName}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleInternalSubmit}>
        <Modal.Body>
          {/* Conditionally render Agent Selector */}
          {!fixedAgentId && (
            <Form.Group className="mb-3">
              <Form.Label>Assign to Agent *</Form.Label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select Agent"
                isClearable
                isLoading={loadingAgents}
                options={agentOptions}
                value={currentAgentOption}
                onChange={handleAgentSelectChange}
                required // Although Select doesn't use HTML5 required, it's good practice
              />
              {!selectedAgentId && error.includes('select an agent') && (
                 <div className="text-danger mt-1 small">{error}</div>
              )}
               <Form.Text className="text-muted">
                 Select the agent who will receive these leads.
               </Form.Text>
            </Form.Group>
          )}

          <p className="small mt-3">
            Upload a CSV file containing leads. Required columns:
            <strong> name, email, mobile, case_type</strong> (Credit Card, Home Loan, Personal Loan, Business Loan, LAP).
            Optional column: <strong>product_id</strong>.
          </p>

          <p className="small mt-3 text-danger">
            Mobile Number and Email id of user must be unique, check before uploading
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Select CSV File *</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              required // HTML5 required validation
              isInvalid={!!error && error.includes('select a file')}
            />
             <Form.Control.Feedback type="invalid">
                {error.includes('select a file') ? error : 'Please select a CSV file.'}
             </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Only CSV files are supported.
            </Form.Text>
          </Form.Group>

          <div className="mt-3 d-flex justify-content-between">
            <Button
              variant="link"
              size="sm"
              onClick={handleDownloadTemplate}
            >
              <FaDownload className="me-1" /> Download Template
            </Button>
          </div>

          {error && !error.includes('select a') && ( // Show general errors
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={uploading || !bulkFile || (!fixedAgentId && !selectedAgentId)}
          >
            {uploading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-1">Uploading...</span>
              </>
            ) : (
              <>
                <FaFileUpload className="me-1" /> Upload Leads
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default BulkUploadModal;
